import * as React from "react";
import Link from "next/link";
import { cn } from "./cn";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "default" | "compact";

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  /** Spoken while loading, e.g. "Saving…". Optional: without it a screen
   *  reader still has the label and `aria-busy`. */
  loadingLabel?: string;
};

// N24 anatomy (docs/n24-porting.md): pill CTAs, medium weight, quiet 1px
// borders, visible focus ring. Colours stay on semantic tokens so the portal
// theme drives them (primary CTA tracks the accent; N24 sets accent = green).
const base =
  "inline-flex items-center justify-center gap-2 select-none font-body text-base font-medium leading-6 rounded-pill transition-colors disabled:cursor-not-allowed disabled:opacity-100 min-h-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

const variantClasses: Record<Variant, string> = {
  primary:
    "bg-gold text-accent-foreground shadow-sm hover:bg-gold-hover active:bg-gold-active disabled:bg-gold-disabled disabled:text-medium-gray",
  secondary:
    "bg-card text-foreground border border-border shadow-sm hover:bg-muted active:bg-light-gray disabled:opacity-50",
  ghost:
    "bg-transparent text-primary hover:bg-primary/5 hover:text-primary rounded-pill px-4 py-2 min-h-10",
  danger:
    "bg-destructive text-white shadow-sm hover:bg-[color:color-mix(in_srgb,var(--color-destructive),black_10%)] active:bg-[color:color-mix(in_srgb,var(--color-destructive),black_18%)]",
};

const sizeClasses: Record<Size, string> = {
  default: "px-6 py-3",
  compact: "px-4 py-2 min-h-10 text-sm",
};

/** The button look on something that is neither `<Button>` nor `<LinkButton>`
 *  — a trigger component that owns its own element. */
export function buttonClass(variant: Variant = "primary", size: Size = "default"): string {
  return cn(base, variantClasses[variant], variant !== "ghost" ? sizeClasses[size] : null);
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "default",
    loading,
    loadingLabel,
    className,
    children,
    disabled,
    ...props
  },
  ref,
) {
  const classes = cn(buttonClass(variant, size), className);
  return (
    <button
      ref={ref}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        // The label used to be REPLACED by "…": the button shrank to three dots
        // (layout shift, in a form, mid-submit) and lost its name. Now the label
        // stays in the box at opacity 0 — same width, still the accessible name —
        // and the spinner sits over it.
        <span className="relative inline-flex items-center justify-center">
          <span className="inline-flex items-center gap-2 opacity-0">{children}</span>
          <span aria-hidden className="absolute inset-0 grid place-items-center">
            <span className="size-5 animate-spin rounded-full border-2 border-current border-r-transparent motion-reduce:animate-none" />
          </span>
          {loadingLabel ? <span className="sr-only">{loadingLabel}</span> : null}
        </span>
      ) : (
        children
      )}
    </button>
  );
});

type LinkButtonProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant?: Variant;
  size?: Size;
};

/**
 * A link dressed as a button. An internal `href` (starts with `/`, no
 * `download`, no foreign `target`) renders `next/link`, so it prefetches and
 * navigates client-side like every other link in the app — it used to be a
 * plain `<a>`, and all 24 callers were internal routes: each "browse courses"
 * or "start" CTA reloaded the whole shell. Anything else stays an anchor.
 */
export function LinkButton({
  variant = "primary",
  size = "default",
  className,
  children,
  href,
  ...props
}: LinkButtonProps) {
  const classes = cn(buttonClass(variant, size), "no-underline", className);
  const internal =
    typeof href === "string" &&
    href.startsWith("/") &&
    props.download == null &&
    (!props.target || props.target === "_self");
  if (internal) {
    return (
      <Link href={href} className={classes} {...props}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={classes} {...props}>
      {children}
    </a>
  );
}
